import { useRef } from "react";

import { Pause, Play, Download, Loader2, HelpCircle } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "~/components/ui/tooltip";
import { cn, getGradientCardStyles } from "~/lib/utils";

// AIDEV-NOTE: Simplified meditation card - Play button starts streaming immediately
// No separate Generate button; clicking Play on a new meditation starts intro + generation
export const MeditationCard: React.FC<{
    title: React.ReactNode;
    description?: string;
    tooltip?: string;
    hidden?: boolean;
    partImageUrl?: string;
    partImageAlt?: string;
    className?: string;
    hasMeditation?: boolean;
    onPlay?: () => void;
    onPause?: () => void;
    onDownload?: () => void | Promise<void>;
    isGenerating?: boolean;
    isPlayingActive?: boolean;
    isPublicSession?: boolean;
    playDisabled?: boolean;
    dataTrackIndex?: number;
    analyticsId?: string;
    // AIDEV-NOTE: Optional control rendered with the Play button (e.g. voice picker).
    // When present the controls stack vertically (control full-width, Play CTA below)
    // on narrow cards, and sit side-by-side (Play right of the control) from @xs
    // card width via container queries.
    leadingControl?: React.ReactNode;
    // AIDEV-NOTE: Collapsed alternative to leadingControl (e.g. the selected-voice
    // chip). When set (and leadingControl is not), the card renders compact:
    // title, then the description on its own row, then inlineControl + Play.
    // The caller toggles between inlineControl and leadingControl to expand/collapse.
    inlineControl?: React.ReactNode;
    // AIDEV-NOTE: Play button variant. Defaults to "ghost"; part check-in cards pass
    // "default" (brand terracotta) to use the primary orange CTA styling.
    playVariant?: "ghost" | "default";
    // Styling props
    applyGradient?: boolean;
    // AIDEV-NOTE: crafted = match commitment cards (white, rounded-[18px], shadow,
    // left-aligned). Opt-in so the session tab's existing look is unchanged.
    crafted?: boolean;
}> = ({
    title,
    description,
    tooltip,
    hidden,
    partImageUrl,
    partImageAlt,
    className,
    hasMeditation,
    onPlay,
    onPause,
    onDownload,
    isGenerating,
    isPlayingActive,
    isPublicSession,
    playDisabled = false,
    dataTrackIndex,
    analyticsId,
    leadingControl,
    inlineControl,
    playVariant = "ghost",
    applyGradient = false,
    crafted = false,
}) => {
    // AIDEV-NOTE: Prevent double-clicks on play/pause button
    const lastClickTime = useRef(0);

    // Compact single-row layout — leadingControl (expanded picker) wins if both are set.
    const hasInline = Boolean(inlineControl) && !leadingControl;

    const handlePlayPauseClick = () => {
        const now = Date.now();
        if (now - lastClickTime.current < 500) {
            // Ignore clicks within 500ms of each other
            return;
        }
        lastClickTime.current = now;

        if (isPlayingActive) {
            onPause?.();
        } else {
            onPlay?.();
        }
    };

    return (
        <div
            className={cn(
                "space-y-0 w-full",
                // AIDEV-NOTE: The card is a container so its control layouts respond to
                // CARD width (it lives in variable-width columns — a narrow desktop rail
                // and a full-width mobile block), not viewport. Scoped to the layouts that
                // actually use @-breakpoints so the plain text/CTA card is untouched.
                // AIDEV-NOTE: DO NOT add @container for the inline layout. Tailwind's
                // @container sets container-type: inline-size, which stops the element
                // sizing from its CONTENTS — inside the session-notes part sidebar that
                // collapsed the whole card to a ~60px column with one character per line
                // (prod, Aug 2026). The same trap is documented in part-meditation-button.
                // The inline layout therefore uses viewport `sm:` breakpoints below.
                leadingControl && "@container",
                hidden && "hidden md:hidden",
            )}
        >
            <div
                className={cn(
                    "flex flex-col gap-4 rounded-lg p-4 relative overflow-hidden",
                    // AIDEV-NOTE: With a leading control (voice picker) the card is a
                    // left-aligned vertical stack (description, voice, play) — it lives in
                    // a narrow column on desktop. With an inline control it stays a column
                    // (title row + compact controls row). Without either it keeps the
                    // original text/CTA row.
                    leadingControl || hasInline ? "" : "md:flex-row",
                    applyGradient
                        ? getGradientCardStyles()
                        : crafted
                          ? "border border-brand-forest-100 bg-white rounded-[18px] shadow-card"
                          : "border border-brand-forest-200 bg-white",
                    className,
                )}
            >
                <div
                    className={cn(
                        "flex w-full flex-col",
                        // AIDEV-NOTE: Left-align + use gap-4 between title and description
                        // (matching the journal input's title spacing) when stacked under
                        // a leading control (narrow column layout).
                        leadingControl ? "gap-4 text-left" : "gap-2",
                        leadingControl
                            ? ""
                            : crafted
                              ? "text-left"
                              : "text-center md:text-left",
                    )}
                >
                    {partImageUrl ? (
                        <div
                            className={cn(
                                "flex items-center gap-4",
                                crafted
                                    ? "justify-start"
                                    : "justify-center md:justify-start",
                            )}
                        >
                            <img
                                src={partImageUrl}
                                alt={partImageAlt || ""}
                                className="size-12 rounded-lg object-cover"
                            />
                            <span className="font-medium flex items-center gap-1.5">
                                {title}
                                {tooltip && (
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <HelpCircle className="size-4 text-muted-foreground cursor-help" />
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p className="max-w-xs">
                                                    {tooltip}
                                                </p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                )}
                            </span>
                        </div>
                    ) : (
                        <span
                            className={cn(
                                "font-medium flex items-center gap-1.5",
                                crafted
                                    ? "justify-start"
                                    : "justify-center md:justify-start",
                            )}
                        >
                            {title}
                            {tooltip && (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <HelpCircle className="size-4 text-muted-foreground cursor-help" />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p className="max-w-xs">
                                                {tooltip}
                                            </p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                        </span>
                    )}
                    {/* AIDEV-NOTE: In the compact (inlineControl) layout the description
                        moves down into the controls row — beside the control on a wide
                        card, on its own row above it on a narrow one. */}
                    {description && !hasInline && (
                        <p className="text-sm text-gray-500">{description}</p>
                    )}
                </div>
                <div
                    className={cn(
                        "flex w-full",
                        // AIDEV-NOTE: Stacked (control above Play) on narrow cards; from
                        // @xs card width the Play CTA moves to the right of the control,
                        // bottom-aligned with the voice tiles (gap matches the tile grid's
                        // gap-1.5 so Play reads as an extra grid column).
                        leadingControl
                            ? "flex-col gap-6 @xs:flex-row @xs:items-end @xs:gap-1.5"
                            : hasInline
                              ? // AIDEV-NOTE: The description takes a full row of its own
                                // at EVERY breakpoint, with the control + Play on the row
                                // beneath it — matching PartJournalCard's
                                // copy-above-button shape. It previously shared the row
                                // from sm up and clamped to two lines, which cut the
                                // sentence off mid-word on a narrow card.
                                "flex-wrap items-center gap-x-4 gap-y-3"
                              : cn(
                                    "gap-2 md:w-auto items-center",
                                    crafted
                                        ? "justify-start"
                                        : "justify-center md:justify-start",
                                ),
                    )}
                >
                    {/* Compact row: description fills, chip + Play take natural width. */}
                    {hasInline && (
                        <>
                            {/* AIDEV-NOTE: The description always takes a full row of its
                                own; the control and Play wrap beneath it. Previously it
                                shared the row from sm up and clamped to two lines, which
                                on a narrow card (sidebar, or a wide viewport with the
                                voice picker present) left it visibly cut off mid-sentence
                                — "designed for…". A one-line summary that gets truncated
                                is worse than one that wraps. */}
                            {description && (
                                <p className="w-full min-w-0 text-sm leading-5 text-gray-500">
                                    {description}
                                </p>
                            )}
                            {inlineControl}
                        </>
                    )}
                    {/* AIDEV-NOTE: Optional leading control (e.g. voice picker);
                        full-width when stacked, flexes (Play takes its natural width)
                        on wide cards. */}
                    {leadingControl && (
                        <div className="w-full @xs:min-w-0 @xs:flex-1">
                            {leadingControl}
                        </div>
                    )}
                    {/* AIDEV-NOTE: When stacked, wrap Play+Download in a full-width row
                        below the control; on wide cards the row shrinks to content and
                        sits right of the control. `contents` keeps the inline layout
                        identical when there is no leading control (Play/Download remain
                        direct flex children). */}
                    <div
                        className={cn(
                            leadingControl
                                ? "flex w-full items-center gap-2 @xs:w-auto @xs:shrink-0"
                                : "contents",
                        )}
                    >
                        {/* AIDEV-NOTE: Play button always visible; starts streaming if no meditation exists */}
                        {/* AIDEV-NOTE: For public sessions, disable play button unless meditation already exists */}
                        <Button
                            variant={playVariant}
                            onClick={handlePlayPauseClick}
                            data-track-index={dataTrackIndex ?? -1}
                            disabled={
                                playDisabled ||
                                (isGenerating && !isPlayingActive) ||
                                (isPublicSession && !hasMeditation)
                            }
                            // AIDEV-NOTE: Full-width CTA in the stacked (leadingControl)
                            // layout; beside the tiles on wide cards it takes natural
                            // width at tile height (h-14), reading as a 7th grid column.
                            className={cn(
                                hasInline ? "shrink-0" : "flex-1",
                                leadingControl
                                    ? "w-full @xs:h-14 @xs:w-auto @xs:flex-none"
                                    : hasInline
                                      ? ""
                                      : "md:flex-none",
                            )}
                            analyticsId={analyticsId}
                        >
                            {isPlayingActive ? (
                                isGenerating ? (
                                    <>
                                        <Loader2 className="mr-1 size-4 animate-spin" />
                                        Playing...
                                    </>
                                ) : (
                                    <>
                                        <Pause className="mr-1 size-4" />
                                        Pause
                                    </>
                                )
                            ) : isGenerating ? (
                                <>
                                    <Loader2 className="mr-1 size-4 animate-spin" />
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <Play className="mr-1 size-4" />
                                    Play
                                </>
                            )}
                        </Button>

                        <Button
                            variant="ghost"
                            onClick={onDownload}
                            className={cn(
                                "flex-1 md:flex-none",
                                (!hasMeditation ||
                                    isPublicSession ||
                                    !onDownload) &&
                                    "hidden",
                            )}
                        >
                            <Download className="mr-1 size-4" />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
